import {
  buildEditorExtensions,
  contentToEditorHtml,
  setClipboardFromSelection,
} from "@components/design-system/molecules/richTextFormatUtils";
import { usePimsExportFormat } from "@hooks";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router";
import remarkGfm from "remark-gfm";

interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

// Remark plugin to parse ++text++ as underline (<u>).
// Sets data.hName so the default mdast→hast conversion emits <u> without
// needing a custom handler.
function remarkUnderline() {
  return (tree: MdastNode) => {
    function visit(node: MdastNode) {
      if (!node.children) return;
      const newChildren: MdastNode[] = [];
      for (const child of node.children) {
        if (child.type === "text" && child.value?.includes("++")) {
          const parts = child.value.split(
            /(\+\+(?:[^+]|\+(?!\+))+\+\+)/,
          );
          for (const part of parts) {
            const match = /^\+\+([\s\S]+?)\+\+$/.exec(part);
            if (match) {
              newChildren.push({
                type: "underline",
                children: [{ type: "text", value: match[1] }],
                data: { hName: "u" },
              });
            } else if (part) {
              newChildren.push({ type: "text", value: part });
            }
          }
        } else {
          newChildren.push(child);
          visit(child);
        }
      }
      node.children = newChildren;
    }
    visit(tree);
  };
}

const COMPANION_MENTION_REGEX = /(@(?:summi|quill|sage))/gi;

const formatMentions = (text: string): React.ReactNode[] => {
  const parts = text.split(COMPANION_MENTION_REGEX);
  return parts.map((part, i) =>
    COMPANION_MENTION_REGEX.test(part) ? (
      <span
        key={i}
        className="bg-brand px-1.5 py-0.5 rounded-sm text-sm font-bold text-white"
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
};

/** Same-origin links navigate via React Router so the Companion provider
 *  (and the active chat) survive the route change. */
const CompanionLink = ({
  href,
  children,
  ...props
}: React.ComponentPropsWithoutRef<"a">) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    if (
      !href ||
      href.startsWith("#") ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.button !== 0
    )
      return;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin === window.location.origin) {
        e.preventDefault();
        navigate(url.pathname + url.search + url.hash);
      }
    } catch {
      // Invalid URL — fall through to default <a> behaviour.
    }
  };

  return (
    <a {...props} href={href} onClick={handleClick}>
      {children}
    </a>
  );
};

const preprocessCompanionContent = (text: string): string => {
  // Strip <br> tags — react-markdown doesn't render raw HTML, so they'd
  // appear as literal text. Replace with a space to preserve word separation.
  let processed = text.replace(/<br\s*\/?>/gi, " ");
  // Make citation brackets visible: [1](url) → [[1]](url)
  processed = processed.replace(
    /\[(\d+)\]\(([^)]+)\)/g,
    String.raw`[\[$1\]]($2)`,
  );
  return processed;
};

// ============================================================================
// Read-only TipTap display that copies selected content as markdown
// ============================================================================

interface MarkdownCopyContentProps {
  content: string;
  className?: string;
  plainText?: boolean;
}

const MarkdownCopyContent = ({
  content,
  className,
  plainText = false,
}: MarkdownCopyContentProps) => {
  const editorRef = useRef<Editor | null>(null);
  const prevContentRef = useRef(content);
  const pimsFormat = usePimsExportFormat();
  const pimsFormatRef = useRef(pimsFormat);
  useEffect(() => {
    pimsFormatRef.current = pimsFormat;
  }, [pimsFormat]);

  const editor = useEditor({
    extensions: buildEditorExtensions(plainText),
    content: contentToEditorHtml(content, plainText),
    editable: false,
    editorProps: {
      attributes: {
        class: `ds-markdown${className ? ` ${className}` : ""}`
          .replace(/\s+/g, " ")
          .trim(),
      },
      handleDOMEvents: {
        copy: (view, event) => {
          const { from, to } = view.state.selection;
          if (from === to) return false;
          setClipboardFromSelection(
            view,
            event,
            editorRef,
            from,
            to,
            pimsFormatRef.current,
          );
          event.preventDefault();
          return true;
        },
      },
    },
    onCreate: ({ editor }) => {
      editorRef.current = editor;
    },
  });

  useEffect(() => {
    if (editor && content !== prevContentRef.current) {
      prevContentRef.current = content;
      editor.commands.setContent(
        contentToEditorHtml(content, plainText),
      );
    }
  }, [content, editor, plainText]);

  return <EditorContent editor={editor} />;
};

// ============================================================================
// Public component
// ============================================================================

interface MarkdownContentProps {
  content: string;
  className?: string;
  isCompanion?: boolean;
  /** When true, content is rendered as plain text with no markdown parsing. */
  plainText?: boolean;
  /** When true, uses a read-only TipTap editor so partial selections are
   *  serialized correctly via the markdown extension and respect pimsExportFormat. */
  copyFormat?: boolean;
}

export const MarkdownContent = ({
  content,
  className = "",
  isCompanion = false,
  plainText = false,
  copyFormat,
}: MarkdownContentProps) => {
  if (copyFormat) {
    return (
      <MarkdownCopyContent
        content={content}
        className={className}
        plainText={plainText}
      />
    );
  }

  const processedContent = isCompanion
    ? preprocessCompanionContent(content)
    : content;

  const companionComponents = isCompanion
    ? {
        // Intercept text nodes to render @mention badges
        p: ({
          children,
          ...props
        }: React.ComponentPropsWithoutRef<"p">) => (
          <span className="block" {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? formatMentions(child)
                : child,
            )}
          </span>
        ),
        // Use client-side navigation for internal links
        a: CompanionLink,
      }
    : undefined;

  const wrapperClass =
    `ds-markdown ${isCompanion ? "font-companion" : ""} ${className}`.trim();

  return (
    <div className={wrapperClass}>
      <ReactMarkdown
        components={companionComponents}
        remarkPlugins={[remarkGfm, remarkUnderline]}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};
